// Camada IndexedDB usando `idb`. Cache local de documents/links/snapshot,
// + dirty queue para sync diferencial.

import { openDB, type IDBPDatabase } from 'idb';

interface SynapseDB {
  documents: {
    key: string;
    value: {
      id: string;
      workspaceId: string;
      data: unknown;
      cachedAt: number;
    };
    indexes: { by_ws: string };
  };
  links: {
    key: string;
    value: {
      id: string;
      workspaceId: string;
      data: unknown;
    };
    indexes: { by_ws: string };
  };
  dirty: {
    // Queue de operações pendentes (offline-first).
    key: string;
    value: {
      id: string;
      workspaceId: string;
      kind: 'doc.upsert' | 'doc.delete' | 'link.create' | 'link.delete' | 'position.update';
      payload: unknown;
      createdAt: number;
      attempts: number;
    };
  };
  meta: {
    key: string;
    value: { key: string; value: unknown };
  };
}

let dbPromise: Promise<IDBPDatabase<SynapseDB>> | null = null;

export function getIdb(): Promise<IDBPDatabase<SynapseDB>> {
  if (!dbPromise) {
    dbPromise = openDB<SynapseDB>('synapse_v1', 1, {
      upgrade(db) {
        const docs = db.createObjectStore('documents', { keyPath: 'id' });
        docs.createIndex('by_ws', 'workspaceId');
        const links = db.createObjectStore('links', { keyPath: 'id' });
        links.createIndex('by_ws', 'workspaceId');
        db.createObjectStore('dirty', { keyPath: 'id' });
        db.createObjectStore('meta', { keyPath: 'key' });
      },
    });
  }
  return dbPromise;
}

export async function cacheDocument(workspaceId: string, doc: { id: string }) {
  const db = await getIdb();
  await db.put('documents', {
    id: doc.id,
    workspaceId,
    data: doc,
    cachedAt: Date.now(),
  });
}

export async function getCachedDocuments(workspaceId: string) {
  const db = await getIdb();
  return db.getAllFromIndex('documents', 'by_ws', workspaceId);
}

export async function enqueueDirty(entry: {
  workspaceId: string;
  kind: SynapseDB['dirty']['value']['kind'];
  payload: unknown;
}) {
  const db = await getIdb();
  const id = crypto.randomUUID();
  await db.put('dirty', {
    id,
    workspaceId: entry.workspaceId,
    kind: entry.kind,
    payload: entry.payload,
    createdAt: Date.now(),
    attempts: 0,
  });
  return id;
}

export async function listDirty(workspaceId: string) {
  const db = await getIdb();
  const all = await db.getAll('dirty');
  return all.filter((d) => d.workspaceId === workspaceId).sort((a, b) => a.createdAt - b.createdAt);
}

export async function consumeDirty(id: string) {
  const db = await getIdb();
  await db.delete('dirty', id);
}

export async function readMeta<T = unknown>(key: string): Promise<T | undefined> {
  const db = await getIdb();
  const row = await db.get('meta', key);
  return row?.value as T | undefined;
}

export async function writeMeta(key: string, value: unknown) {
  const db = await getIdb();
  await db.put('meta', { key, value });
}
